'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  Phone, MessageSquare, Zap, Crown, CalendarCheck, CheckCircle2,
  Clock, Star, ArrowRight, AlertCircle } from
'lucide-react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';


const AVAILABILITY = [
{ day: 'Mon', slots: ['9am', '11am', '2pm'], key: 'avail-mon' },
{ day: 'Tue', slots: ['10am', '3pm'], key: 'avail-tue' },
{ day: 'Wed', slots: [], key: 'avail-wed' },
{ day: 'Thu', slots: ['9am', '1pm', '4pm'], key: 'avail-thu' },
{ day: 'Fri', slots: ['9am', '11am'], key: 'avail-fri' },
{ day: 'Sat', slots: ['10am'], key: 'avail-sat' },
{ day: 'Sun', slots: [], key: 'avail-sun' }];


const RECENT_ACTIVITY = [
{ id: 'act-001', text: 'Completed boiler service in SW4', time: '2 hrs ago', icon: CheckCircle2, color: 'text-mint-500' },
{ id: 'act-002', text: 'New 5★ review received', time: '5 hrs ago', icon: Star, color: 'text-amber-500' },
{ id: 'act-003', text: 'Emergency callout in SW8', time: 'Yesterday', icon: AlertCircle, color: 'text-orange-500' },
{ id: 'act-004', text: 'Gas Safety cert issued in SW11', time: '2 days ago', icon: CheckCircle2, color: 'text-mint-500' }];


const SIMILAR_PROVIDERS = [
{
  id: 'sim-001',
  name: 'Clearwater Plumbing',
  trade: 'Plumber',
  rating: 4.4,
  distance: '4.4 mi',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11c8c04d2-1772209925689.png",
  avatarAlt: 'Danny Clarke plumber headshot'
},
{
  id: 'sim-002',
  name: 'Northern Gas Solutions',
  trade: 'Gas Engineer',
  rating: 4.8,
  distance: '4.2 mi',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_140e6a184-1763296753137.png",
  avatarAlt: 'Raj Patel gas engineer headshot'
},
{
  id: 'sim-003',
  name: 'Metro Tiling Specialists',
  trade: 'Tiler',
  rating: 4.8,
  distance: '2.1 mi',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_179ebd6f2-1763294255544.png",
  avatarAlt: 'Carlos Diaz tiler headshot'
}];


export default function ProfileSidebar() {
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const handleBookCall = () => {
    toast.success('Connecting you via Vapi voice AI — James will answer shortly');
  };

  const handleInstantQuote = () => {
    toast.success('Instant quote form opened');
  };

  const handleRequestQuote = () => {
    toast.success('Quote request sent to James');
  };

  const handleSlotSelect = (day: string, slot: string) => {
    setSelectedSlot(`${day}-${slot}`);
    toast.success(`Availability request sent for ${day} ${slot}`);
  };

  return (
    <div className="space-y-5">
      {/* Primary CTA card */}
      <div className="bg-white rounded-2xl border border-amber-200 shadow-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Crown size={16} className="text-amber-600" />
          <span className="text-xs font-semibold text-amber-700">Premium Provider</span>
        </div>
        <h3 className="font-display font-700 text-gray-900 text-base mb-1">Ready to hire James?</h3>
        <p className="text-gray-500 text-sm mb-4">
          As a Premium provider, James offers instant voice booking and AI-generated quotes.
        </p>
        <div className="space-y-2">
          <button
            onClick={handleBookCall}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold text-sm hover:from-amber-600 hover:to-amber-700 transition-all active:scale-95">

            <Phone size={15} />
            Book a Call — AI answers instantly
          </button>
          <button
            onClick={handleInstantQuote}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-plum-50 text-plum-800 border border-plum-200 font-semibold text-sm hover:bg-plum-100 transition-all active:scale-95">

            <Zap size={15} />
            Get an Instant Quote
          </button>
          <button
            onClick={handleRequestQuote}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition-all active:scale-95">

            <MessageSquare size={15} />
            Send a Message
          </button>
        </div>
        <p className="text-gray-400 text-xs text-center mt-3">
          Free to contact — no subscription needed
        </p>
      </div>

      {/* Availability */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarCheck size={16} className="text-plum-600" />
          <h3 className="font-display font-700 text-gray-900 text-sm">This week&apos;s availability</h3>
        </div>
        <div className="space-y-2">
          {AVAILABILITY.map((day) =>
          <div key={day.key} className="flex items-center gap-2">
              <span className={`text-xs font-semibold w-7 ${day.slots.length > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
                {day.day}
              </span>
              <div className="flex items-center gap-1.5 flex-1">
                {day.slots.length > 0 ?
              day.slots.map((slot) => {
                const key = `${day.day}-${slot}`;
                const isSelected = selectedSlot === key;
                return (
                  <button
                    key={`slot-${day.key}-${slot}`}
                    onClick={() => handleSlotSelect(day.day, slot)}
                    className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all ${
                    isSelected ?
                    'bg-mint-500 text-white' : 'bg-mint-50 text-mint-700 hover:bg-mint-100 border border-mint-200'}`
                    }>

                        {slot}
                      </button>);

              }) :

              <span className="text-xs text-gray-300 italic">Fully booked</span>
              }
              </div>
            </div>
          )}
        </div>
        <p className="text-gray-400 text-xs mt-3 flex items-center gap-1">
          <Clock size={11} />
          Slots update in real time · Emergency callouts available 24/7
        </p>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
        <h3 className="font-display font-700 text-gray-900 text-sm mb-3">Recent activity</h3>
        <div className="space-y-3">
          {RECENT_ACTIVITY.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.id} className="flex items-start gap-2.5">
                <Icon size={14} className={`${item.color} flex-shrink-0 mt-0.5`} />
                <div className="min-w-0">
                  <p className="text-gray-700 text-xs leading-snug">{item.text}</p>
                  <p className="text-gray-400 text-xs">{item.time}</p>
                </div>
              </div>);

          })}
        </div>
      </div>

      {/* Similar providers */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-700 text-gray-900 text-sm">Similar providers nearby</h3>
          <Link href="/provider-directory" className="text-xs text-plum-600 hover:text-plum-800 font-medium flex items-center gap-0.5">
            See all <ArrowRight size={11} />
          </Link>
        </div>
        <div className="space-y-3">
          {SIMILAR_PROVIDERS.map((p) =>
          <Link
            key={p.id}
            href="/public-provider-profile"
            className="flex items-center gap-3 group hover:bg-gray-50 rounded-xl p-2 -mx-2 transition-colors">

              <AppImage
              src={p.avatar}
              alt={p.avatarAlt}
              width={36}
              height={36}
              className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-800 truncate group-hover:text-plum-700 transition-colors">
                  {p.name}
                </p>
                <p className="text-gray-400 text-xs">{p.trade} · {p.distance}</p>
              </div>
              <div className="flex items-center gap-0.5 text-xs text-gray-600 flex-shrink-0">
                <Star size={11} className="text-amber-400 fill-amber-400" />
                <span className="tabular-nums">{p.rating}</span>
              </div>
            </Link>
          )}
        </div>
      </div>

      {/* Trust signals */}
      <div className="bg-plum-50 border border-plum-100 rounded-2xl p-4">
        <h3 className="font-display font-700 text-plum-900 text-sm mb-3">Why trust LoveLocals?</h3>
        <div className="space-y-2">
          {[
          { id: 'trust-1', text: 'All reviews are verified — only paying customers can leave one' },
          { id: 'trust-2', text: 'Certifications checked by our team before display' },
          { id: 'trust-3', text: 'No fake profiles — providers claim real listings' },
          { id: 'trust-4', text: 'LoveLocals never touches payments — you deal direct' }].
          map((item) =>
          <div key={item.id} className="flex items-start gap-2">
              <CheckCircle2 size={13} className="text-mint-500 flex-shrink-0 mt-0.5" />
              <p className="text-plum-800 text-xs leading-snug">{item.text}</p>
            </div>
          )}
        </div>
      </div>
    </div>);

}